import { coreService } from './core-service'
import { PromoCodeValidation } from '../dao/core-dao'

const STORAGE_KEY = 'grnd_promo_code'
const TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

type StoredPromoCode = {
  code: string
  discountLabel: string | null
  // Stripe product IDs the promo is restricted to. `null`/absent means it applies to every plan.
  appliesToProductIds?: string[] | null
  expiresAt: number
}

export type ResolvedPromoCode = {
  code: string
  discountLabel: string | null
  appliesToProductIds: string[] | null
}

export const promoCodeService = {
  validate: async (code: string): Promise<PromoCodeValidation> => {
    return coreService.validatePromoCode(code)
  },

  store: (params: { code: string; discountLabel: string | null; appliesToProductIds?: string[] | null }): void => {
    const entry: StoredPromoCode = {
      code: params.code,
      discountLabel: params.discountLabel,
      appliesToProductIds: params.appliesToProductIds ?? null,
      expiresAt: Date.now() + TTL_MS,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  },

  get: (): ResolvedPromoCode | null => {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    try {
      const entry: StoredPromoCode = JSON.parse(raw)
      if (Date.now() > entry.expiresAt) {
        promoCodeService.clear()
        return null
      }
      return {
        code: entry.code,
        discountLabel: entry.discountLabel,
        appliesToProductIds: entry.appliesToProductIds ?? null,
      }
    } catch {
      promoCodeService.clear()
      return null
    }
  },

  /**
   * Re-validate the stored promo against the backend and refresh its cached
   * discount label + product restriction. Clears it if it's no longer valid.
   * No-op (returns null) when nothing is stored. Used to heal entries persisted
   * before the applies-to restriction was captured.
   */
  refresh: async (): Promise<ResolvedPromoCode | null> => {
    const stored = promoCodeService.get()
    if (!stored) return null
    try {
      const { valid, discountLabel, appliesToProductIds } = await coreService.validatePromoCode(stored.code)
      if (!valid) {
        promoCodeService.clear()
        return null
      }
      promoCodeService.store({ code: stored.code, discountLabel, appliesToProductIds })
      return promoCodeService.get()
    } catch {
      // Network/validation hiccup — keep the cached entry rather than dropping the discount.
      return stored
    }
  },

  /**
   * Whether the stored promo applies to a given Stripe product. A promo with no
   * product restriction (`appliesToProductIds === null`) applies to everything.
   * Returns `false` when no promo is stored.
   */
  appliesToProduct: (productId: string): boolean => {
    const promo = promoCodeService.get()
    if (!promo) return false
    return promo.appliesToProductIds === null || promo.appliesToProductIds.includes(productId)
  },

  clear: (): void => {
    localStorage.removeItem(STORAGE_KEY)
  },
}
